import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import {
  loginWithEmail,
  forgotPasswordEmail,
  type ForgotPasswordOutcome,
} from '@/services/auth/authService';
import {
  getUserDetails,
  updateUserDetails,
  prefetchDynamicForms,
  toCandidateApiRequest,
  type CandidateDetails,
} from '@/services/api/authApi';
import { candidateDetailsFromJson, candidateDetailsToJson } from '@/services/api/profileApi';
import { isHandledByErrorInterceptor } from '@/services/api/apiClient';
import { analyticsService } from '@/services/analytics/analyticsService';
import { ROUTES } from '@/app/router/routes';
import { resolvePostLoginRedirect, type LoginRedirectState } from '@/services/auth/postLoginRedirect';

/**
 * useLogin — orchestrates the full login flow.
 *
 * Mirrors the Flutter WebLoginBloc._OnLoginWithEmail + WebLoginPageConsumer listener:
 *   1. Reset MFA verification, authenticate via Firebase
 *   2. Pre-fetch form content, fetch user details from backend
 *   3. Email not verified → /signUp, which shows the verify-email card
 *   4. Tenant has MFA enabled → /mfa (the OTP is required on every login)
 *   5. Otherwise navigate to the intended destination
 *
 * Every navigation logs the screen_view Flutter's NavigationService.navigateTo() logs.
 *
 * Google / Apple / phone login exist in Flutter but are not reachable from the web
 * login page, so they are not migrated.
 *
 * A 409 from the candidate calls is left to the apiClient interceptor, which navigates
 * to /duplicateAccount (Flutter HttpInterCeptor), so the flow stops there.
 */

export interface LoginState {
  isLoading: boolean;
  /** Flutter's message for the failed login (untranslated) */
  error: string;

  handleEmailLogin: (email: string, password: string) => Promise<void>;
  handleForgotPassword: (email: string) => Promise<ForgotPasswordOutcome>;
  clearError: () => void;
}

export function useLogin(): LoginState {
  const navigate = useNavigate();
  const location = useLocation();
  const { content } = useTenantStore();
  const { setMfaVerified } = useAuthStore();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const clearError = useCallback(() => setError(''), []);

  /**
   * Post-login: fetch user details, check MFA, navigate.
   * Mirrors Flutter WebLoginBloc._OnLoginWithEmail success handler
   * and WebLoginPageConsumer listener.
   */
  const handlePostLogin = useCallback(
    async (user: User, email: string) => {
      // Carried through to /mfa so it can return to the same destination
      const returnState = (location.state ?? {}) as LoginRedirectState;

      prefetchDynamicForms();

      let userDetails: CandidateDetails | null = null;
      try {
        userDetails = await getUserDetails();
      } catch (error) {
        if (isHandledByErrorInterceptor(error)) return;
        // No candidate record yet — Flutter creates it with the login email
        // (CandidateDetails.fromJson({primaryEmailId}).toJson()). Flutter's spinner then
        // never stops; React carries on to the destination instead.
        try {
          await updateUserDetails(candidateDetailsToJson(candidateDetailsFromJson({ primaryEmailId: email })));
        } catch (updateError) {
          if (isHandledByErrorInterceptor(updateError)) return;
        }
      }

      if (!user.emailVerified) {
        void analyticsService.logScreenView({ screenName: ROUTES.SIGN_UP });
        navigate(ROUTES.SIGN_UP, { state: returnState });
        return;
      }

      const isTenantMfaEnabled = content?.isMfaEnabled ?? false;
      if (isTenantMfaEnabled) {
        // Email MFA users never have Firebase-enrolled factors, so Flutter treats every
        // login as needing the OTP and resets the backend flag until it is verified again
        // (WebLoginEvent.onUpdateUserDetails → updateUserDetails(apiRequest: true)).
        if (userDetails?.isMfaEnabled) {
          updateUserDetails(toCandidateApiRequest({
            primaryEmailId: userDetails.primaryEmailId,
            workspaceId: userDetails.workspaceId,
            isMfaEnabled: false,
          })).catch(() => {/* non-critical */});
        }

        void analyticsService.logScreenView({ screenName: ROUTES.MFA });
        navigate(ROUTES.MFA, { state: { ...returnState, email: user.email } });
        return;
      }

      // Navigate to the intended destination (Flutter ProfileInput routing) or home
      const redirect = resolvePostLoginRedirect(returnState);
      void analyticsService.logScreenView({ screenName: redirect.screenName, screenClass: redirect.screenClass });
      navigate(redirect.path, { replace: true, state: redirect.state });
    },
    [navigate, location.state, content],
  );

  // ── Email/Password ─────────────────────────────────────────────

  const handleEmailLogin = useCallback(
    async (email: string, password: string) => {
      setError('');
      setIsLoading(true);
      try {
        setMfaVerified(false);
        const outcome = await loginWithEmail(email, password);
        void analyticsService.logEvent('LOGIN', 'EMAIL_LOGIN');
        if (!outcome.success) {
          setError(outcome.message);
          return;
        }
        await handlePostLogin(outcome.user, email);
      } finally {
        setIsLoading(false);
      }
    },
    [handlePostLogin, setMfaVerified],
  );

  // ── Forgot Password ────────────────────────────────────────────

  /**
   * Mirrors Flutter WebLoginBloc._ForgotPasswordEmailTriggeredEvent. When the link is sent the
   * bloc emits isSuccess with isWebForgotPassword false, so the form closes and
   * WebLoginPageConsumer's isSuccess listener navigates on the ProfileInput, else to /home.
   * (`from`, set by ProtectedRoute, has no Flutter counterpart and would lead back to /login
   * while signed out, so it isn't used here.)
   */
  const handleForgotPassword = useCallback(
    async (email: string): Promise<ForgotPasswordOutcome> => {
      setError('');
      setIsLoading(true);
      try {
        const outcome = await forgotPasswordEmail(email);
        if (outcome === 'sent') {
          void analyticsService.logEvent('forgot_password_page', 'forgot_password_email_sent', `email: ${email}`);
          const { from: _from, ...profileInput } = (location.state ?? {}) as LoginRedirectState;
          const redirect = resolvePostLoginRedirect(profileInput);
          void analyticsService.logScreenView({ screenName: redirect.screenName, screenClass: redirect.screenClass });
          navigate(redirect.path, { replace: true, state: redirect.state });
        }
        return outcome;
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, location.state],
  );

  return {
    isLoading,
    error,
    handleEmailLogin,
    handleForgotPassword,
    clearError,
  };
}
